import { drinkName } from './customerMenu';
import { useEffect, useRef, useState } from 'react';
import type { CartItem, MenuProduct, OrderView } from '../shared/domain';
import { useI18n } from './i18n';
import { emptySavedDrinks, readSavedDrinks, writeSavedDrinks, saveFavorite, removeFavorite, rememberPaidOrder, validateSavedItem, type SavedDrinks as SavedState, type SavedFavorite } from './savedDrinks';

export function useSavedDrinks(key: string) {
  function read() { try { return { key, value: readSavedDrinks(key), error: false }; } catch { return { key, value: emptySavedDrinks(), error: true }; } }
  const [state, setState] = useState(read);
  if (state.key !== key) setState(read());
  const currentKey = useRef(key); currentKey.current = key;
  function change(update: (current: SavedState) => SavedState) {
    if (state.key !== key || currentKey.current !== key) return false;
    try {
      const value = update(readSavedDrinks(key));
      writeSavedDrinks(key, value);
      setState({ key, value, error: false });
      return true;
    } catch { setState(current => ({ ...current, error: true })); return false; }
  }
  return { value: state.key === key ? state.value : emptySavedDrinks(), error: state.key === key && state.error,
    save: (item: CartItem, name: string, id?: string) => change(current => saveFavorite(current, item, name, id)),
    remove: (id: string) => change(current => removeFavorite(current, id)) };
}

export function SavedDrinks({ saved, products, busy, onEdit, onAdd }: {
  saved: ReturnType<typeof useSavedDrinks>; products: MenuProduct[]; busy: boolean;
  onEdit: (favorite: SavedFavorite) => void; onAdd: (items: CartItem[]) => Promise<boolean>;
}) {
  const { t, locale, translateOptionLabel } = useI18n();
  const [message, setMessage] = useState('');
  const [issues, setIssues] = useState<string[]>([]);
  function summary(item: CartItem) {
    const product = products.find(product => product.id === item.productId);
    const selections = item.selections;
    const bean = product?.options.beans.find(bean => bean.id === selections.beanId);
    return [product?.temperature && t(`temperature.${product.temperature}`),
      selections.beanId && (bean?.name ?? t('saved.changed')),
      t(({ none: 'option.noIce', less: 'option.lessIce', normal: 'option.normalIce', extra: 'option.extraIce' })[selections.ice]), product && product.options.sweetness.length > 1 ? t('option.addedSweetener', { value: selections.sweetness }) : undefined,
      t(selections.packaging === 'own_cup' ? 'option.ownCup' : 'option.takeaway'),
      ...selections.extras.map(id => product?.options.extras.find(extra => extra.id === id)?.name ?? t('saved.changed'))].filter(Boolean).map(translateOptionLabel).join(' · ');
  }
  async function add(items: { name: string; item: CartItem }[]) {
    setMessage('');
    const problems = items.flatMap(({ name, item }) => {
      const result = validateSavedItem(item, products);
      return result.issues.map(issue => `${name}: ${t(issue === 'missing' ? 'saved.missing' : issue === 'unavailable' ? 'saved.unavailable' : 'saved.changed')}`);
    });
    setIssues(problems);
    if (problems.length) return;
    if (await onAdd(items.map(({ item }) => item))) setMessage(t('saved.added'));
  }
  if (!saved.value.favorites.length && !saved.value.recentOrders.length && !saved.error) return null;
  return <section className="saved-drinks" aria-label={t('saved.title')}>
    <h2>{t('saved.title')}</h2><p>{t('saved.description')}</p>
    {saved.error && <p className="error" role="alert">{t('saved.storageError')}</p>}
    {!saved.value.favorites.length && <p className="fine">{t('saved.empty')}</p>}
    <div className="saved-drinks-list">{saved.value.favorites.map(favorite => <article className="saved-drink" key={favorite.id}>
      <strong>{drinkName(locale, favorite.name)}</strong><span>{favorite.item.quantity}×</span><p className="fine">{summary(favorite.item)}</p>
      <div className="saved-drink-actions">
        <button className="secondary" disabled={busy || !products.some(p => p.id === favorite.item.productId)} onClick={() => onEdit(favorite)}>{t('saved.edit')}</button>
        <button className="secondary" disabled={busy} onClick={() => { saved.remove(favorite.id); setMessage(''); }}>{t('saved.remove')}</button>
        <button className="secondary" disabled={busy} onClick={() => void add([favorite])}>{t('saved.add')}</button>
      </div>
    </article>)}</div>
    {!!saved.value.recentOrders.length && <h3>{t('saved.recentTitle')}</h3>}
    <div className="saved-drinks-list">{saved.value.recentOrders.map(order => <article className="saved-drink" key={order.id}>
      <strong>{t('saved.orderLabel', { number: order.number })}</strong>
      <div>{order.items.map(({ name, item }, index) => <p className="fine" key={index}>{item.quantity}× {drinkName(locale, name)}<br />{summary(item)}</p>)}</div>
      <button className="secondary" disabled={busy} onClick={() => void add(order.items)}>{t('saved.reorder')}</button>
    </article>)}</div>
    {issues.length > 0 && <div className="saved-issues" role="alert"><p>{t('saved.reviewRequired')}</p><ul>{issues.map((issue, index) => <li key={index}>{issue}</li>)}</ul></div>}
    {message && <p className="saved-message" role="status">{message}</p>}
  </section>;
}

export function PaidOrderMemory({ order, storageKey, enabled = true }: { order: OrderView; storageKey: string; enabled?: boolean }) {
  const { t } = useI18n();
  const [error, setError] = useState(false);
  // Bind this receipt to its original browser/account scope. Signing in while
  // viewing someone else's tracking link must not copy their order into an account.
  const initialKey = useRef(storageKey);
  useEffect(() => {
    if (!enabled || order.paymentStatus !== 'paid' || initialKey.current !== storageKey) return;
    try {
      const current = readSavedDrinks(storageKey);
      const next = rememberPaidOrder(current, order);
      if (next !== current) writeSavedDrinks(storageKey, next);
      setError(false);
    } catch { setError(true); }
  }, [order, storageKey, enabled]);
  return error ? <p className="error" role="alert">{t('saved.storageError')}</p> : null;
}
